import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, onAuthStateChanged, signOut } from '../lib/firebase';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const fetchDbUser = useCallback(async (idToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      setDbUser(response.data);
      return response.data;
    } catch (error) {
      console.log('User not in database yet');
      return null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const idToken = await firebaseUser.getIdToken();
        setToken(idToken);
        await fetchDbUser(idToken);
      } else {
        setUser(null);
        setDbUser(null);
        setToken(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchDbUser]);

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setDbUser(null);
    setToken(null);
  };

  const refreshToken = async () => {
    if (user) {
      const newToken = await user.getIdToken(true);
      setToken(newToken);
      return newToken;
    }
    return null;
  };

  // Refresh user data from database (for credits update)
  const refreshUser = useCallback(async () => {
    if (token) {
      return await fetchDbUser(token);
    }
    return null;
  }, [token, fetchDbUser]);

  const value = {
    user,
    dbUser,
    setDbUser,
    loading,
    token,
    logout,
    refreshToken,
    refreshUser,
    isAdmin: dbUser?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
